import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { UnsavedChangesDialog } from "@/components/forms/UnsavedChangesDialog";
import { TwoStepGate } from "@/components/auth/TwoStepGate";
import { AppSurface } from "@/components/AppSurface";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { BrandProvider } from "@/contexts/BrandContext";
import { WaitlistProvider } from "@/contexts/WaitlistContext";
import { RouteGate } from "@/components/RouteGate";
import { ServiceErrorBoundary } from "@/components/ErrorBoundary";
import { Sentry } from "@/lib/sentry";
import { Analytics } from "@vercel/analytics/react";
import { RealtimeSupervisor } from "@/lib/realtime/RealtimeSupervisor";

/**
 * Every page loads on demand. Until 2026-11-02 all 39 were imported here, so a
 * couple opening one page downloaded the whole dashboard: first paint 11.6 s on
 * a slow phone, against 2.3 s on 23 Studios (THE_LIST #72). Guard:
 * src/parity/pagesLoadOnDemand.parity.test.ts.
 */
const HomePage = lazy(() => import("./pages/HomePage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Auth = lazy(() => import("./pages/Auth"));
const Bypass = lazy(() => import("./pages/Bypass"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const BusinessOperations = lazy(() => import("./pages/BusinessOperations"));
const Booking = lazy(() => import("./pages/Booking"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const RatesPage = lazy(() => import("./pages/RatesPage"));
const RatesGigPage = lazy(() => import("./pages/RatesGigPage"));
const PortalRecover = lazy(() => import("./pages/PortalRecover"));
const CrewInvite = lazy(() => import("./pages/CrewInvite"));
const DeliveryPresentation = lazy(() => import("./pages/DeliveryPresentation"));
const EmbedPackagesShowcase = lazy(() => import("./pages/EmbedPackagesShowcase"));
const SitePreview = lazy(() => import("./pages/SitePreview"));
const SiteHome = lazy(() => import("./pages/SiteHome"));
const SiteAboutPage = lazy(() => import("./pages/SiteAboutPage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const PortfolioStoryPage = lazy(() => import("./pages/PortfolioStoryPage"));
const Partnerships = lazy(() => import("./pages/Partnerships"));
const GearCheck = lazy(() => import("./pages/GearCheck"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const LegalNotice = lazy(() => import("./pages/legal/LegalNotice"));
const PricingPage = lazy(() => import("./pages/marketing/PricingPage"));
const ThePricePage = lazy(() => import("./pages/marketing/ThePricePage"));
const BookingPage = lazy(() => import("./pages/marketing/BookingPage"));
const CouplesPage = lazy(() => import("./pages/marketing/CouplesPage"));
const JourneyPage = lazy(() => import("./pages/marketing/JourneyPage"));
const PaymentsPage = lazy(() => import("./pages/marketing/PaymentsPage"));
const YourSitePage = lazy(() => import("./pages/marketing/YourSitePage"));
const ManifestoPage = lazy(() => import("./pages/marketing/ManifestoPage"));
const StoryPage = lazy(() => import("./pages/marketing/StoryPage"));
const SecurityPage = lazy(() => import("./pages/marketing/SecurityPage"));
const IntegrationsPage = lazy(() => import("./pages/marketing/IntegrationsPage"));
const ChangelogPage = lazy(() => import("./pages/marketing/ChangelogPage"));
const HelpPage = lazy(() => import("./pages/marketing/HelpPage"));
const BringYourHistoryPage = lazy(() => import("./pages/marketing/BringYourHistoryPage"));
const SwitchingFromPage = lazy(() => import("./pages/marketing/SwitchingFromPage"));
const ProblemsPage = lazy(() => import("./pages/marketing/ProblemsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours — keeps cache alive for persistence
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

const persister = createSyncStoragePersister({
  storage: window.localStorage,
  throttleTime: 1000,
});

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    // A refused sign-up (the invite-only hook) comes back from Google on this
    // URL as error_description; the sign-in page shows it with the waitlist.
    const search = new URLSearchParams(location.search);
    const hash = new URLSearchParams(location.hash.replace(/^#/, ''));
    const refused = search.get('error_description') ?? hash.get('error_description');
    return <Navigate to={refused ? `/auth?error_description=${encodeURIComponent(refused)}` : '/auth'} replace />;
  }

  // Once two-factor authentication is on, the app opens only after the code (Security).
  return <TwoStepGate>{children}</TwoStepGate>;
};

// The standalone Availability page left for the dashboard on 2026-10-15: it
// rendered outside the shell, with no menu and no way back (Jota: "Availability
// Management sends me to disconnected pages"). Its three tabs each have one home
// in the shell now: the capacity policy in Settings, the Google calendar card in
// Settings → Calendar, the calendar itself in Schedule. Old links and bookmarks
// land there, the #booking-fees anchor kept (signedInPagesLiveInTheShell.parity).
const AvailabilityRedirect = () => {
  const location = useLocation();
  const tab = new URLSearchParams(location.search).get('tab');
  const to =
    tab === 'capacity' ? '/dashboard?tab=settings&section=capacity'
    : tab === 'overview' ? '/dashboard?tab=schedule'
    : '/dashboard?tab=settings&section=calendar';
  return <Navigate to={`${to}${location.hash}`} replace />;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

/**
 * Reads the authenticated user from AuthProvider and mounts the realtime
 * supervisor scoped to that user. Must live inside AuthProvider AND inside
 * the QueryClient provider — both are required for the hooks it uses.
 */
const RealtimeSupervisorMount = () => {
  const { user } = useAuth();
  return <RealtimeSupervisor userId={user?.id ?? null} />;
};

const App = () => (
  <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please reload the page.</p>}>
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      maxAge: 1000 * 60 * 60 * 24,
      // Every deploy invalidates the stored cache. Without this the 24h cache
      // outlives the code that produced it, and a user keeps seeing pre-fix
      // data until they think to hard-refresh.
      buster: __APP_BUILD__,
    }}
  >
    <AuthProvider>
      <RealtimeSupervisorMount />
      <SubscriptionProvider>
        <TooltipProvider>
          <Sonner />
          <UnsavedChangesDialog />
          <BrowserRouter>
            {/* Cookieless visit measurement — no cookies, no localStorage, no
                consent surface required. Replaces the PostHog/Sentry-replay
                tracking that previously ran on Tier B public pages. */}
            <Analytics />
            <WaitlistProvider>
            <Suspense fallback={null}>
            <Routes>
            {/* Public marketing home — the waitlist gate lives here */}
            <Route path="/" element={<HomePage />} />

            {/* A colleague's door (2026-11-02): the creator's rate card and the private
                link of one gig. Outside RouteGate: a colleague has no preview key. */}
            <Route path="/rates/gig/:token" element={<RatesGigPage />} />
            <Route path="/rates/:handle" element={<RatesPage />} />

            {/* Preview bypass — grants localStorage access when ?key=... matches VITE_PREVIEW_KEY */}
            <Route path="/preview" element={<Bypass />} />

            {/* Legal surfaces — public by law (GDPR Arts. 13/14, DL 7/2004),
                so intentionally OUTSIDE RouteGate: the waitlist on "/" already
                collects emails pre-launch. */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal" element={<LegalNotice />} />

            {/* The showroom (2026-11-01): the marketing pages behind the home.
                Public like the home, outside RouteGate; each one is built as its
                own indexable file (scripts/prerender-marketing.mjs); the app stays noindex. The map
                the header and footer read is src/lib/marketing/siteNav.ts. */}
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/the-price" element={<ThePricePage />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/couples-page" element={<CouplesPage />} />
            <Route path="/journey" element={<JourneyPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/your-site" element={<YourSitePage />} />
            <Route path="/manifesto" element={<ManifestoPage />} />
            <Route path="/story" element={<StoryPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/bring-your-history" element={<BringYourHistoryPage />} />
            <Route path="/switching-from" element={<SwitchingFromPage />} />
            <Route path="/switching-from/:tool" element={<SwitchingFromPage />} />
            <Route path="/problems" element={<ProblemsPage />} />

            {/* Public gear-check — cross-user stolen-gear lookup. Intentionally
                outside RouteGate so non-Contruly visitors can verify serials. */}
            <Route path="/gear-check" element={<GearCheck />} />

            {/* Every non-marketing route is behind RouteGate until public launch.
                RouteGate lets the preview key or a signed-in session through
                and redirects everyone else to /.
                Remove RouteGate wrappers at launch to fully open the app. */}
            {/* Sign-in and the dashboard left the preview gate on 2026-10-04:
                the door is the server's invite-only hook (who may CREATE an
                account), and the dashboard needs an account anyway. */}
            {/* AppSurface: the creator's app renders at 90 % (src/lib/appSurface.ts,
                THE_LIST #55); a client's pages below are never inside it. */}
            <Route path="/auth" element={
              <AppSurface>
                <PublicRoute>
                  <Auth />
                </PublicRoute>
              </AppSurface>
            } />
            <Route path="/services" element={<RouteGate><LandingPage /></RouteGate>} />
            <Route path="/dashboard" element={
              <AppSurface>
                <ProtectedRoute>
                  <BrandProvider>
                    <ServiceErrorBoundary>
                      <Dashboard />
                    </ServiceErrorBoundary>
                  </BrandProvider>
                </ProtectedRoute>
              </AppSurface>
            } />
            {/* Legacy standalone route: its pieces live in the shell (see AvailabilityRedirect). */}
            <Route path="/availability" element={<AvailabilityRedirect />} />
            {/* Legacy standalone route — the canonical home is the in-shell tab.
                Redirect so old bookmarks don't land on a chrome-less twin. */}
            <Route path="/business-operations" element={<Navigate to="/dashboard?tab=operations" replace />} />
            <Route path="/partnerships" element={
              <RouteGate>
                <AppSurface>
                  <ProtectedRoute>
                    <BrandProvider>
                      <Partnerships />
                    </BrandProvider>
                  </ProtectedRoute>
                </AppSurface>
              </RouteGate>
            } />
            <Route path="/book/:handle/:audienceSlug" element={<RouteGate><Booking /></RouteGate>} />
            <Route path="/book/:handle" element={<RouteGate><Booking /></RouteGate>} />
            <Route path="/book" element={<RouteGate><Booking /></RouteGate>} />
            <Route path="/booking-confirmation" element={<RouteGate><BookingConfirmation /></RouteGate>} />
            <Route path="/portal" element={<RouteGate><ClientPortal /></RouteGate>} />
            <Route path="/portal/recover" element={<RouteGate><PortalRecover /></RouteGate>} />
            <Route path="/crew/:token" element={<RouteGate><CrewInvite /></RouteGate>} />
            <Route path="/delivery/:deliverableId" element={<RouteGate><DeliveryPresentation /></RouteGate>} />
            <Route path="/embed/:handle/:audienceSlug" element={<RouteGate><EmbedPackagesShowcase /></RouteGate>} />
            <Route path="/site-preview/:handle" element={<RouteGate><SiteHome /></RouteGate>} />
            {/* Brand-level pages sit ABOVE the audience tier — About is about the
                creator, not an audience. A static segment outranks :audienceSlug,
                so this needs no resolver change; it does mean 'about' must stay a
                reserved audience slug. */}
            <Route path="/site-preview/:handle/about" element={<RouteGate><SiteAboutPage /></RouteGate>} />
            <Route path="/site-preview/:handle/:audienceSlug" element={<RouteGate><SitePreview /></RouteGate>} />
            {/* Story detail must outrank the generic :pageSlug route below. */}
            <Route path="/site-preview/:handle/:audienceSlug/real-stories/:storySlug" element={<RouteGate><PortfolioStoryPage /></RouteGate>} />
            <Route path="/site-preview/:handle/:audienceSlug/options/:optionSlug" element={<RouteGate><BrandPage /></RouteGate>} />
            <Route path="/site-preview/:handle/:audienceSlug/:pageSlug" element={<RouteGate><BrandPage /></RouteGate>} />

            {/* Website-mirroring routes: /:handle/for/:audienceSlug/... */}
            {/* Mirrors user's Squarespace: BRANDDOMAIN.COM/for-loving-couples/details/booking */}
            <Route path="/:handle/for/:audienceSlug/details/booking" element={<RouteGate><Booking /></RouteGate>} />
            <Route path="/:handle/for/:audienceSlug/details/pricing" element={<RouteGate><EmbedPackagesShowcase /></RouteGate>} />
            <Route path="/:handle/for/:audienceSlug/booking" element={<RouteGate><Booking /></RouteGate>} />
            <Route path="/:handle/for/:audienceSlug" element={<RouteGate><EmbedPackagesShowcase /></RouteGate>} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </WaitlistProvider>
          </BrowserRouter>
        </TooltipProvider>
      </SubscriptionProvider>
    </AuthProvider>
  </PersistQueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
